/**
 * SunDirectionLegend Component
 *
 * Displays a categorical legend for sun direction indicator lines
 */

import { useMemo } from 'react'
import styled from 'styled-components'
import { getSunDirectionLegendConfig } from '../config/sunDirectionLegendConfig'

export interface SunDirectionLegendProps {
  /** Layer ID to display legend for */
  layerId: string
  /** Optional CSS class name */
  className?: string
}

/**
 * SunDirectionLegend Component
 *
 * Renders a categorical legend showing sunrise, sunset, and current sun direction colors
 */
export const SunDirectionLegend = ({
  layerId,
  className,
}: SunDirectionLegendProps) => {
  const legendConfig = useMemo(
    () => getSunDirectionLegendConfig(layerId),
    [layerId]
  )

  if (!legendConfig) {
    return null
  }

  return (
    <LegendContainer className={className}>
      <LegendTitle>{legendConfig.title}</LegendTitle>
      <CategoricalLegend>
        {legendConfig.entries.map(
          (entry: { color: string; label: string }, index: number) => (
            <CategoryItem key={index}>
              <CategoryColor $color={entry.color} />
              <CategoryLabel>{entry.label}</CategoryLabel>
            </CategoryItem>
          )
        )}
      </CategoricalLegend>
      <HelpText>Lines radiate from map center showing sun direction</HelpText>
    </LegendContainer>
  )
}

// Styled Components

const LegendContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`

const LegendTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const CategoricalLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CategoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CategoryColor = styled.div<{ $color: string }>`
  width: 16px;
  height: 3px;
  background-color: ${({ $color }) => $color};
  border-radius: 1px;
  flex-shrink: 0;
`

const CategoryLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.3;
`

const HelpText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.small};
  font-style: italic;
`
